import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchLostSales } from '../api/drilldown';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { localYMD } from '../lib/format';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { t } from '../lib/i18n';
import type { LostSale, User } from '../api/types';
import Header from '../components/Header';
import DrillCell from '../components/DrillCell';

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

export default function LostSales() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <LostSalesInner user={user} /> : <Navigate to="/login" replace />;
}

function LostSalesInner({ user }: { user: User }) {
  const [from, setFrom] = useState(dateNDaysAgo(7));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [data, setData] = useState<LostSale[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Shared header store (lib/storeFilter); staff stay locked to their store.
  const [storeFilter] = useStoreFilter(user);

  const load = () => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchLostSales({
      from,
      to,
      sid: storeFilter ?? undefined,
    })
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => load(), [from, to, storeFilter]);

  const totals = useMemo(() => {
    if (!data) return { count: 0, qty: 0, value: 0 };
    let qty = 0, value = 0;
    for (const r of data) { qty += r.lsqty; value += r.value_mxn; }
    return { count: data.length, qty, value };
  }, [data]);

  const pg = usePagination(data);

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.lost.title}</Title>
        <Text className="text-tremor-content text-sm">{t.lost.subtitle}</Text>

        <Card className="space-y-4">
          <DateRangePresets
            from={from}
            to={to}
            onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }}
          />
          <div className="flex items-end gap-1 text-sm justify-end">
            <Text>
              {totals.count} {t.lost.summary} · {totals.qty} {t.lost.unitsSuffix} · {fmtMxn.format(totals.value)} {t.lost.lostSuffix}
            </Text>
          </div>
        </Card>

        {error && <ErrorRetry message={error} onRetry={load} />}
        {loading && !data && <TableSkeleton rows={6} cols={6} />}
        {data && data.length === 0 && !loading && <EmptyState message={t.lost.empty} />}
        {data && data.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.lost.cols.date}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.ticket}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.account}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.store}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.slid}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.lost.cols.pdesc}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.lostCount}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.ytdhits}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.curhits}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.lsqty}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.sell}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.lost.cols.value}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pg.pageRows.map((row, i) => (
                  <TableRow key={`${row.sid}-${row.tnum}-${row.line}-${row.part}-${i}`}>
                    <TableCell>{row.idate}</TableCell>
                    <TableCell>
                      <DrillCell target={{ kind: 'tkt', sid: row.sid, tnum: row.tnum }}>
                        {row.tnum}
                      </DrillCell>
                    </TableCell>
                    <TableCell>
                      <DrillCell target={row.anum > 0 ? { kind: 'account', anum: row.anum } : null}>
                        {row.anum}
                      </DrillCell>
                    </TableCell>
                    <TableCell>{row.sid}</TableCell>
                    <TableCell>{row.slid || '—'}</TableCell>
                    <TableCell className="font-mono">
                      {/* JCD-59: was /parts?q=<part> — a substring search that
                          dropped both the line and this row's store. */}
                      <DrillCell target={{ kind: 'inv', sid: row.sid, line: row.line, part: row.part }}>
                        {row.part}
                      </DrillCell>
                    </TableCell>
                    <TableCell>{row.pdesc}</TableCell>
                    <TableCell className="text-right">{row.lost_count}</TableCell>
                    <TableCell className="text-right">{row.ytdhits}</TableCell>
                    <TableCell className="text-right">{row.curhits}</TableCell>
                    <TableCell className="text-right">{row.lsqty}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(row.sell_mxn)}</TableCell>
                    <TableCell className="text-right text-red-600 dark:text-red-400 font-semibold">
                      {fmtMxn.format(row.value_mxn)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination {...pg} className="px-4" />
          </Card>
        )}
      </main>
    </div>
  );
}
